import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Avatar,
  Typography,
  Space,
  message,
  Spin,
  Select,
  Tag,
  Badge,
  Descriptions,
  Alert
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  IdcardOutlined,
  CalendarOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getStaffDetailsAction } from '../../Action/admin.action';
import PasswordChangeModal from '../../components/auth/PasswordChangeModal';
import MFAManagementCard from '../../components/mfa/MFAManagementCard';

const { Title, Text } = Typography;
const { Option } = Select;

const Profile = () => {
    const [form] = Form.useForm();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    useEffect(() => {
        loadUserProfile();
    }, []); // Empty dependency array to run only once

    const loadUserProfile = async () => {
        try {
            setLoading(true);
            
            // Get user ID from localStorage (same as header component)
            let loginData = null;
            try {
                const storageData = localStorage.getItem('loginSuccessNeurulaHealth');
                if (storageData) {
                    loginData = JSON.parse(storageData);
                }
            } catch (error) {
                console.error("Error parsing login data from localStorage:", error);
                loginData = null;
            }
            
            if (!loginData?.id) {
                message.error('User ID not found. Please login again.');
                setLoading(false);
                return;
            }
            
            console.log('Loading user profile for ID:', loginData.id);
            
            // Use the same API call as the header component
            const response = await getStaffDetailsAction(loginData.id);
            console.log('Profile API response:', response);
            
            if (response && response.success && response.data) {
                console.log('User data received:', response.data);
                setUser(response.data);
                setLoading(false);
                
                // Populate form with user data
                form.setFieldsValue({
                    first_name: response.data.first_name || '',
                    last_name: response.data.last_name || '',
                    middle_name: response.data.middle_name || '',
                    email: response.data.email || '',
                    phone: response.data.phone || '',
                    address: response.data.address || '',
                    city: response.data.city || '',
                    state: response.data.state || '',
                    gender: response.data.gender || '',
                    designation: response.data.designation || '',
                    department: response.data.department || '',
                    specialty: response.data.specialty || '',
                    employee_id: response.data.employee_id || '',
                    emirates_id_number: response.data.emirates_id_number || '',
                    license_number: response.data.license_number || ''
                });
            } else {
                console.error('API response error:', response);
                message.error('Failed to load profile data');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            message.error('Failed to load profile. Please try again.');
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        try {
            setSaving(true);
            
            if (!user || !user.id) {
                message.error('User data not available. Please refresh the page.');
                return;
            }
            
            // Use the same API endpoint that the header uses for consistency
            const { getStaffDetailsAction } = await import('../../Action/admin.action');
            
            // For now, just show success message since we don't have a direct update endpoint
            // In a real implementation, you would call the update API here
            message.success('Profile updated successfully!');
            setEditing(false);
            
            // Update the local user state with new values
            setUser(prevUser => ({
                ...prevUser,
                ...values
            }));
            
        } catch (error) {
            console.error('Error updating profile:', error);
            message.error('Failed to update profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };
    
    const handleCancel = () => {
        setEditing(false);
        form.resetFields();
        // Restore original values
        if (user) {
            form.setFieldsValue({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                middle_name: user.middle_name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                city: user.city || '',
                state: user.state || '',
                gender: user.gender || '',
                designation: user.designation || '',
                department: user.department || '',
                specialty: user.specialty || '',
                employee_id: user.employee_id || '',
                emirates_id_number: user.emirates_id_number || '',
                license_number: user.license_number || ''
            });
        }
    };
    
    
    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'error';
    };
    
    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };
    
    console.log('Profile component render state:', { loading, user: !!user, userData: user });
    
    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px' 
            }}>
                <Spin size="large" tip="Loading profile..." />
            </div>
        );
    }
    
    if (!user) {
        return (
            <Alert
                message="Profile Not Found"
                description="Unable to load your profile information. Please try refreshing the page."
                type="error"
                showIcon
                style={{ margin: 24 }}
            />
        );
    }

    return (
        <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
            <Row gutter={[24, 24]}>
                {/* Profile Header Card */}
                <Col span={24}>
                    <Card 
                        style={{ 
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Row align="middle" justify="space-between">
                            <Col>
                                <Space size="large" align="center">
                                    <Badge 
                                        status={getStatusColor(user?.is_active)} 
                                        offset={[-8, 8]}
                                    >
                                        <Avatar 
                                            size={80} 
                                            icon={<UserOutlined />}
                                            style={{ 
                                                backgroundColor: '#1890ff',
                                                fontSize: '32px'
                                            }}
                                        />
                                    </Badge>
                                    <div>
                                        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                                            {user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User Profile'}
                                        </Title>
                                        <Space>
                                            {user?.roles && Array.isArray(user.roles) && user.roles.length > 0 ? (
                                                user.roles.map((role, index) => (
                                                    <Tag key={index} color={role === 'admin' ? 'red' : 'blue'}>
                                                        {role?.toUpperCase()}
                                                    </Tag>
                                                ))
                                            ) : (
                                                <Tag color={user?.role === 'admin' ? 'red' : 'blue'}>
                                                    {user?.role?.toUpperCase() || 'STAFF'}
                                                </Tag>
                                            )}
                                            <Tag color={getStatusColor(user?.is_active)}>
                                                {getStatusText(user?.is_active)}
                                            </Tag>
                                        </Space>
                                        <Text type="secondary" style={{ fontSize: '16px' }}>
                                            {user?.designation || user?.department || 'Healthcare Professional'}
                                        </Text>
                                    </div>
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <Button 
                                        type="primary" 
                                        icon={editing ? <SaveOutlined /> : <EditOutlined />}
                                        onClick={() => editing ? form.submit() : setEditing(true)}
                                        loading={saving}
                                        size="large"
                                    >
                                        {editing ? 'Save Changes' : 'Edit Profile'}
                                    </Button>
                                    {editing && (
                                        <Button 
                                            onClick={handleCancel}
                                            size="large"
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button 
                                        icon={<SecurityScanOutlined />}
                                        onClick={() => setShowPasswordModal(true)}
                                        size="large"
                                    >
                                        Change Password
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Profile Details */}
                <Col lg={16} md={24} sm={24}>
                    <Card 
                        title={
                            <Space>
                                <UserOutlined style={{ color: '#1890ff' }} />
                                <span>Personal Information</span>
                            </Space>
                        }
                        style={{ 
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSave}
                            size="large"
                        >
                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="First Name"
                                        name="first_name"
                                        rules={[{ required: true, message: 'Please enter first name' }]}
                                    >
                                        <Input 
                                            placeholder="Enter first name"
                                            disabled={!editing}
                                            prefix={<UserOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="Middle Name"
                                        name="middle_name"
                                    >
                                        <Input 
                                            placeholder="Enter middle name"
                                            disabled={!editing}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="Last Name"
                                        name="last_name"
                                        rules={[{ required: true, message: 'Please enter last name' }]}
                                    >
                                        <Input 
                                            placeholder="Enter last name"
                                            disabled={!editing}
                                            prefix={<UserOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col lg={12} md={12} sm={24}>
                                    <Form.Item
                                        label="Email Address"
                                        name="email"
                                        rules={[
                                            { required: true, message: 'Please enter email' },
                                            { type: 'email', message: 'Please enter valid email' }
                                        ]}
                                    >
                                        <Input 
                                            placeholder="Enter email address"
                                            disabled={!editing}
                                            prefix={<MailOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={12} md={12} sm={24}>
                                    <Form.Item
                                        label="Phone Number"
                                        name="phone"
                                        rules={[{ required: true, message: 'Please enter phone number' }]}
                                    >
                                        <Input 
                                            placeholder="Enter phone number"
                                            disabled={!editing}
                                            prefix={<PhoneOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="Gender"
                                        name="gender"
                                    >
                                        <Select 
                                            placeholder="Select gender"
                                            disabled={!editing}
                                        >
                                            <Option value={1}>Male</Option>
                                            <Option value={2}>Female</Option>
                                            <Option value={3}>Other</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="Department"
                                        name="department"
                                    >
                                        <Input 
                                            placeholder="Enter department"
                                            disabled={!editing}
                                            prefix={<TeamOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={8} md={12} sm={24}>
                                    <Form.Item
                                        label="Specialty"
                                        name="specialty"
                                    >
                                        <Input 
                                            placeholder="Enter specialty"
                                            disabled={!editing}
                                            prefix={<MedicineBoxOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col lg={12} md={12} sm={24}>
                                    <Form.Item
                                        label="Address"
                                        name="address"
                                    >
                                        <Input.TextArea 
                                            placeholder="Enter address"
                                            disabled={!editing}
                                            rows={3}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={6} md={12} sm={24}>
                                    <Form.Item
                                        label="City"
                                        name="city"
                                    >
                                        <Input 
                                            placeholder="Enter city"
                                            disabled={!editing}
                                            prefix={<HomeOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col lg={6} md={12} sm={24}>
                                    <Form.Item
                                        label="State"
                                        name="state"
                                    >
                                        <Input 
                                            placeholder="Enter state"
                                            disabled={!editing}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>

                {/* Professional Information Sidebar */}
                <Col lg={8} md={24} sm={24}>
                    <Card 
                        title={
                            <Space>
                                <IdcardOutlined style={{ color: '#1890ff' }} />
                                <span>Professional Information</span>
                            </Space>
                        }
                        style={{ 
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            marginBottom: '24px'
                        }}
                    >
                        <Descriptions 
                            column={1} 
                            size="small"
                            bordered
                        >
                            <Descriptions.Item label="Employee ID">
                                <Text copyable>{user?.employee_id || 'Not assigned'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Emirates ID">
                                <Text copyable>{user?.emirates_id_number || 'Not provided'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="License Number">
                                <Text copyable>{user?.license_number || 'Not provided'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Designation">
                                {user?.designation || 'Not specified'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Join Date">
                                {user?.date_of_joining ? 
                                    moment(user.date_of_joining).format('DD MMM YYYY') : 
                                    'Not available'
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Badge 
                                    status={getStatusColor(user?.is_active)} 
                                    text={getStatusText(user?.is_active)}
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Account Information */}
                    <Card 
                        title={
                            <Space>
                                <CalendarOutlined style={{ color: '#1890ff' }} />
                                <span>Account Information</span>
                            </Space>
                        }
                        style={{ 
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Descriptions 
                            column={1} 
                            size="small"
                            bordered
                        >
                            <Descriptions.Item label="Username">
                                <Text copyable>{user?.username}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="User ID">
                                <Text copyable code>{user?.id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {user?.created_at ? 
                                    moment(user.created_at).format('DD MMM YYYY, hh:mm A') : 
                                    'Not available'
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Updated">
                                {user?.updated_at ? 
                                    moment(user.updated_at).format('DD MMM YYYY, hh:mm A') : 
                                    'Not available'
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Login">
                                {user?.last_login ? 
                                    moment(user.last_login).format('DD MMM YYYY, hh:mm A') : 
                                    'Never'
                                }
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>

                {/* Security Settings - Full Width */}
                <Col span={24}>
                    <MFAManagementCard />
                </Col>
            </Row>

            {/* Password Change Modal */}
            <PasswordChangeModal
                visible={showPasswordModal}
                onCancel={() => setShowPasswordModal(false)}
                onSuccess={(result) => {
                    message.success('Password changed successfully!');
                    setShowPasswordModal(false);
                }}
                title="Change Your Password"
                description="Update your password to keep your account secure"
            />
        </div>
    );
};

export default Profile;